<template>
  <div>
    <div class="cm-scroll">
      <table class="cm-table">
        <thead>
          <tr>
            <th class="cm-corner" :style="{ zIndex: names.length + 3 }"></th>
            <!-- each label leans over the headers to its right, so earlier
                 columns stack above later ones to keep it from being covered -->
            <th
              v-for="(name, j) in names"
              :key="'cm-h-' + j"
              class="cm-colhead"
              :style="{ zIndex: names.length - j + 2 }"
              :title="name"
            >
              <span class="cm-colhead-text">{{ name }}</span>
            </th>
            <!-- room for the last labels to lean into -->
            <th class="cm-colhead cm-colhead-spacer"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in cells" :key="'cm-r-' + i">
            <th class="cm-rowhead" :title="names[i]">{{ names[i] }}</th>
            <td
              v-for="(cell, j) in row"
              :key="'cm-c-' + i + '-' + j"
              :class="cellClass(cell, i, j)"
              :style="cellStyle(cell)"
              :title="cellTitle(cell, i, j)"
              @click="onClick(cell, i, j)"
            >
              <template v-if="!cell.diagonal && cell.r !== null">
                {{ formatR(cell.r) }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="d-flex align-center flex-wrap mt-2 cm-caption">
      <span class="mr-1">{{ formatR(-1) }}</span>
      <span class="cm-scale" :style="{ background: scaleGradient }"></span>
      <span class="ml-1 mr-4">+1.00</span>
      <span class="cm-swatch cm-hatched mr-1"></span>
      <span class="mr-4">fewer than {{ minPairs }} subjects with both</span>
      <span
        v-if="adjustedFor.length > 0"
        class="cm-swatch cm-diagonal mr-1"
      ></span>
      <span v-if="adjustedFor.length > 0" class="mr-4">covariate</span>
      <span>cell = {{ methodSymbol }} &middot; click a cell for detail</span>
    </div>
  </div>
</template>

<script>
import {
  METHOD_SPEARMAN,
  MIN_PAIRS,
  correlationColor,
  correlationTextColor,
  formatR,
} from '@/utils/correlation';

// stops sampled along the colour scale for the legend bar
const SCALE_STOPS = 11;

export default {
  name: 'CorrelationHeatmap',
  props: {
    // display names, parallel to the rows and columns of `cells`
    names: {
      type: Array,
      required: true,
    },
    // square matrix of {r, n, diagonal?, coupled?} from correlationMatrix()
    // and markCoupled()
    cells: {
      type: Array,
      required: true,
    },
    method: {
      type: String,
      required: true,
    },
    // display names of the covariates every cell is adjusted for
    adjustedFor: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      minPairs: MIN_PAIRS,
    };
  },
  computed: {
    methodSymbol() {
      const symbol =
        this.method === METHOD_SPEARMAN ? 'Spearman ρ' : 'Pearson r';
      if (this.adjustedFor.length === 0) return symbol;
      return `partial ${symbol}, adjusted for ${this.adjustedFor.join(', ')}`;
    },
    shortSymbol() {
      const symbol = this.method === METHOD_SPEARMAN ? 'ρ' : 'r';
      return this.adjustedFor.length > 0 ? `partial ${symbol}` : symbol;
    },
    scaleGradient() {
      const stops = [];
      for (let i = 0; i < SCALE_STOPS; i += 1) {
        const r = -1 + (2 * i) / (SCALE_STOPS - 1);
        stops.push(correlationColor(r));
      }
      return `linear-gradient(to right, ${stops.join(', ')})`;
    },
  },
  methods: {
    formatR,
    usable(cell) {
      return !cell.diagonal && !cell.covariate && cell.r !== null;
    },
    // a variable that is also a covariate has nothing left to correlate once
    // adjusted for itself, and a variable against its own change (see
    // isCoupledPair) is left blank, so both read like the diagonal
    inert(cell) {
      return cell.diagonal || cell.covariate || cell.coupled;
    },
    cellClass(cell) {
      const inert = this.inert(cell);
      return {
        'cm-cell': true,
        'cm-diagonal': inert,
        'cm-hatched': !inert && cell.r === null,
        'cm-clickable': !inert,
      };
    },
    cellStyle(cell) {
      if (!this.usable(cell)) return {};
      return {
        backgroundColor: correlationColor(cell.r),
        color: correlationTextColor(cell.r),
      };
    },
    cellTitle(cell, i, j) {
      if (cell.diagonal) return `${this.names[i]}: n = ${cell.n}`;
      const pair = `${this.names[i]} × ${this.names[j]}`;
      if (cell.coupled) return `${pair}: n = ${cell.n}`;
      if (cell.covariate) {
        return `${pair}: not shown, one of these is adjusted for`;
      }
      if (cell.r === null) {
        return `${pair}: n = ${cell.n}, too few subjects with all values`;
      }
      if (cell.rUnadjusted !== undefined) {
        return (
          `${pair}: unadjusted ${formatR(cell.rUnadjusted)} → ` +
          `${this.shortSymbol} ${formatR(cell.r)}, n = ${cell.n}`
        );
      }
      return `${pair}: ${this.shortSymbol} = ${formatR(cell.r)}, n = ${cell.n}`;
    },
    // a hatched cell still opens, so the user can see why it is empty
    onClick(cell, i, j) {
      if (this.inert(cell)) return;
      this.$emit('select', { row: i, col: j });
    },
  },
};
</script>

<style scoped>
.cm-caption {
  font-size: 12px;
  color: #777;
}
/* headers stay pinned while a wide matrix scrolls under them, as in the
   Outcome by predictor matrix */
.cm-scroll {
  overflow: auto;
  max-height: 70vh;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.cm-table {
  border-collapse: separate;
  border-spacing: 0;
}
.cm-table th {
  background-color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  color: #333;
}
/* z-index is set inline, above every column header */
.cm-corner {
  position: sticky;
  top: 0;
  left: 0;
}
/* z-index is set inline, falling from left to right */
.cm-colhead {
  position: sticky;
  top: 0;
  height: 112px;
  padding: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
/* names lean up and to the right at 45 degrees, as the visit labels do on the
   Visits bubble chart, starting from the middle of their column */
.cm-colhead-text {
  position: absolute;
  left: 50%;
  bottom: 6px;
  display: block;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  transform-origin: 0 100%;
  transform: rotate(-45deg);
}
/* a 140px label at 45 degrees reaches about 100px right of its column */
.cm-colhead-spacer {
  width: 100px;
  min-width: 100px;
  border-bottom: none;
}
.cm-rowhead {
  position: sticky;
  left: 0;
  z-index: 1;
  padding: 0 8px;
  text-align: right;
  white-space: nowrap;
  max-width: 14em;
  overflow: hidden;
  text-overflow: ellipsis;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}
.cm-cell {
  width: 52px;
  min-width: 52px;
  height: 40px;
  padding: 0;
  text-align: center;
  font-family: monospace;
  font-size: 12px;
  border-right: 1px solid #ffffff;
  border-bottom: 1px solid #ffffff;
}
.cm-clickable {
  cursor: pointer;
}
.cm-clickable:hover {
  outline: 2px solid #3f51b5;
  outline-offset: -2px;
}
.cm-diagonal {
  background-color: #eeeeee;
}
.cm-hatched {
  background: repeating-linear-gradient(
    45deg,
    #f5f5f5,
    #f5f5f5 4px,
    #dddddd 4px,
    #dddddd 6px
  );
}
.cm-scale {
  display: inline-block;
  width: 140px;
  height: 10px;
  border: 1px solid rgba(0, 0, 0, 0.15);
}
.cm-swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 1px solid rgba(0, 0, 0, 0.15);
}
</style>
